import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Shield } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user.role !== "admin") {
        setError("This account does not have admin access");
        return;
      }
      localStorage.setItem("ulti_admin_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 50% 45% at 50% 35%, rgba(139,92,246,0.15), transparent 70%)" }} />
      <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0D0B12]/90 backdrop-blur-xl p-8">
        <div className="flex items-center gap-2 justify-center">
          <Shield className="w-6 h-6 text-[#8B5CF6]" strokeWidth={2.5} />
          <span className="font-display text-2xl tracking-tight text-white">ULTI<span className="text-[#A78BFA]">SMP</span></span>
        </div>
        <p className="mt-2 text-center text-xs tracking-[0.3em] text-slate-500">ADMIN ACCESS</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-slate-400 text-xs tracking-[0.15em]">EMAIL</Label>
            <Input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]"
              placeholder="admin@ultismp.net"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-slate-400 text-xs tracking-[0.15em]">PASSWORD</Label>
            <Input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="bg-white/5 border-white/10 text-white focus-visible:ring-[#8B5CF6]"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            SIGN IN
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-back-to-store-link"
          className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to store
        </Link>
      </div>
    </main>
  );
}
